import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Eye, Ban, CheckCircle2, Share2 } from "lucide-react";
import { api, errMsg, useApi, copyShareLink } from "@/lib/api";
import { Page, Spinner, Empty, UserAvatar } from "@/components/common";
import { fmtDate } from "@/components/EventCard";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const Kpi = ({ v, l, testId }) => (
  <div className="rounded-2xl bg-white border border-stone-200 p-5"><p data-testid={testId} className="font-display text-3xl font-black">{v}</p><p className="font-mono text-[10px] uppercase tracking-widest text-stone-500">{l}</p></div>
);

const Attendee = ({ a, eventId, onDone }) => {
  const u = a.user || a;
  const sid = a.signup_id || a.id;
  const checked = a.checked_in || a.checked_in_at;
  const checkIn = async () => {
    try { await api.post(`/events/${eventId}/checkin/${sid}`); toast.success(`${u.name} checked in`); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid={`attendee-${sid}`} className="flex items-center gap-3 py-3 border-b border-stone-100 last:border-0">
      <UserAvatar user={u} className="h-9 w-9" />
      <div className="flex-1 min-w-0"><p className="font-medium truncate">{u.name || a.name}</p><p className="text-xs text-stone-500 truncate">{a.email || u.email || ""}{a.phone ? ` · ${a.phone}` : ""}</p></div>
      {checked ? <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold"><CheckCircle2 className="h-4 w-4" /> In</span> : (
        <button data-testid={`attendee-checkin-btn-${sid}`} onClick={checkIn} className="px-4 py-1.5 rounded-full bg-stone-900 text-white text-xs font-semibold hover:bg-[#FF5C00] transition-colors">Check in</button>
      )}
    </div>
  );
};

export default function ManageEvent() {
  const { id } = useParams();
  const { data: ev, isLoading, refetch: refetchEv } = useApi(`/events/${id}`);
  const { data: dash, refetch } = useApi(`/events/${id}/dashboard`);
  if (isLoading) return <Page><Spinner /></Page>;
  const e = ev?.event;
  if (!e) return <Page><Empty title="Event not found" /></Page>;

  const cancel = async () => {
    try { const { data } = await api.post(`/events/${id}/cancel`); toast.success(`Event cancelled${data.refunded ? ` · ${data.refunded} refunded` : ""}`); refetchEv(); refetch(); } catch (err) { toast.error(errMsg(err)); }
  };
  const attendees = dash?.attendees || [];

  return (
    <Page className="max-w-5xl">
      <p className="font-mono text-xs uppercase tracking-widest text-stone-500">Manage · {fmtDate(e.start_time)}</p>
      <h1 data-testid="manage-event-title" className="font-display text-4xl sm:text-5xl font-black tracking-tight">{e.title}</h1>
      {e.cancelled && <p data-testid="manage-cancelled-badge" className="mt-3 inline-block font-mono text-xs bg-red-600 text-white px-3 py-1 rounded-full">Cancelled</p>}
      <div className="mt-6 flex flex-wrap gap-3">
        <Link to={`/events/${id}`} data-testid="manage-view-btn" className="inline-flex items-center gap-2 border border-stone-300 hover:border-stone-900 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"><Eye className="h-4 w-4" /> View page</Link>
        <button data-testid="manage-share-btn" onClick={() => copyShareLink(id)} className="inline-flex items-center gap-2 border border-stone-300 hover:border-stone-900 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"><Share2 className="h-4 w-4" /> Share link</button>
        {!e.cancelled && <Link to={`/events/${id}/edit`} data-testid="manage-edit-btn" className="inline-flex items-center gap-2 border border-stone-300 hover:border-stone-900 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"><Pencil className="h-4 w-4" /> Edit</Link>}
        {!e.cancelled && (
          <AlertDialog>
            <AlertDialogTrigger data-testid="manage-cancel-btn" className="inline-flex items-center gap-2 border border-red-300 text-red-600 hover:bg-red-50 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors"><Ban className="h-4 w-4" /> Cancel event</AlertDialogTrigger>
            <AlertDialogContent className="rounded-3xl">
              <AlertDialogHeader><AlertDialogTitle>Cancel this event?</AlertDialogTitle><AlertDialogDescription>Attendees will be refunded. This can't be undone.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel data-testid="manage-cancel-dismiss">Keep it</AlertDialogCancel><AlertDialogAction data-testid="manage-cancel-confirm" onClick={cancel} className="bg-red-600 hover:bg-red-700">Cancel event</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
      <div className="mt-8 grid grid-cols-3 gap-4">
        <Kpi v={`$${Number(dash?.revenue || 0).toFixed(2)}`} l="Revenue" testId="manage-revenue" />
        <Kpi v={dash?.attendee_count ?? 0} l="Going" testId="manage-attendee-count" />
        <Kpi v={dash?.checked_in ?? 0} l="Checked in" testId="manage-checked-in" />
      </div>
      <section className="mt-8 rounded-3xl bg-white border border-stone-200 p-6">
        <h2 className="font-display text-2xl font-bold mb-2">Attendees</h2>
        {attendees.length === 0 ? <p className="text-sm text-stone-500" data-testid="manage-no-attendees">No one has signed up yet.</p> : attendees.map((a, i) => <Attendee key={a.signup_id || a.id || i} a={a} eventId={id} onDone={refetch} />)}
      </section>
    </Page>
  );
}
